// src/components/kuestion/shared/AiPromptBox.jsx
//
// "Need questions? Ask an AI" (web mockup, 17 Sep 2026; import added 4 Oct
// 2026). The prompt is composed from the template being edited, so it is
// always in step with the paper. It is a copyable string, not a model call: no
// key, no backend, and the questions stay the school's.
//
// The JSON it asks for is the schema the bank upload validates (shared/
// bankJson.js, and the server behind it): examCode, module, type
// "multichoice", totalQuestions, and per question `question`, `type`,
// `options` as "A. …" strings, `correctAnswer`, `explanation`. A prompt that
// asked for anything else would produce a file the upload rejects.
//
// "Import the reply" closes the loop inside the editor: paste what the
// assistant wrote, or choose the saved file, and it becomes a bank on this
// template. Same check as the Question banks upload, all-or-nothing — never
// half a bank. The host does the upload and the attach (`onImport`); with no
// host handler the box is the prompt alone.
import React, { useState } from 'react';
import { parseBankReply } from './bankJson';

const codeFrom = (prefix, name) => {
  const slug = String(name || 'PAPER').toUpperCase().replace(/[^A-Z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 14) || 'PAPER';
  return prefix ? `${prefix}-${slug}` : slug;
};

export function buildBankPrompt({ orgName, prefix, name, questions, modules = [], passOn, passValue }) {
  const n = Number(questions) || 0;
  const passNum = parseInt(passValue, 10);
  const passLine = passOn
    ? (!Number.isNaN(passNum) && passNum >= 0 && passNum <= 100 ? `${passNum}%` : '(set a percentage)')
    : 'none — score only, no verdict';
  const modLine = modules.length
    ? `${modules.join(', ')} — split the questions evenly across them`
    : '(name your modules) — split the questions evenly across them';
  const examCode = codeFrom(prefix, name);
  return [
    'Write a question bank for the Kuestion exam platform.',
    '',
    'EXAM',
    ...(orgName ? [`  Organisation   ${orgName}`] : []),
    `  Paper          ${name || 'Untitled'}`,
    `  Questions      ${n || '(set a number)'}`,
    `  Modules        ${modLine}`,
    `  Pass mark      ${passLine}`,
    '',
    'FORMAT',
    '  Multiple choice. Exactly four options. Exactly one correct.',
    '  One file per module. The module name is what the strength analytics are',
    '  built from, so spell it identically in every file that belongs to it.',
    '  Every question carries an explanation saying why the correct option is',
    '  right AND why the most tempting wrong option is wrong.',
    '',
    'RETURN valid JSON only — no prose, no code fences, nothing before or after:',
    '',
    '  {',
    `    "examCode": "${examCode}",`,
    '    "module": "Module name",',
    '    "type": "multichoice",',
    '    "totalQuestions": 0,',
    '    "questions": [',
    '      {',
    '        "id": 1,',
    '        "question": "...",',
    '        "type": "multichoice",',
    '        "options": ["A. ...", "B. ...", "C. ...", "D. ..."],',
    '        "correctAnswer": "B",',
    '        "explanation": "..."',
    '      }',
    '    ]',
    '  }',
    '',
    '  "totalQuestions" must equal the number of entries in "questions".',
    '  Each option starts with its letter and a full stop. "correctAnswer" is',
    '  the letter A–D of the right option.',
    '',
    'AVOID',
    '  Questions answerable from the wording alone, "all of the above",',
    '  double negatives, and anything that needs a diagram to answer.',
    '',
    'DELIVERY',
    `  Give me each module as a downloadable .json file, UTF-8, named like`,
    `  "${examCode}_module-name.json". If you cannot attach files, print the raw`,
    '  JSON as your entire reply so I can save it myself.',
  ].join('\n');
}

// FileReader rather than File#text(): it is the one every browser has.
const readFile = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(String(r.result || ''));
  r.onerror = () => reject(new Error('unreadable'));
  r.readAsText(file);
});

function ImportReply({ onImport }) {
  const [text, setText] = useState('');
  const [errors, setErrors] = useState(null);
  const [done, setDone] = useState(null); // { count, module, attached, why }
  const [busy, setBusy] = useState(false);

  const run = async (raw) => {
    if (busy) return;
    const parsed = parseBankReply(raw);
    if (!parsed.ok) { setErrors(parsed.errors); setDone(null); return; }
    setBusy(true); setErrors(null);
    try {
      const r = await onImport(parsed);
      if (r && r.ok) { setText(''); setDone({ count: parsed.count, module: parsed.module, attached: r.attached !== false, why: r.why }); }
      else { setDone(null); setErrors((r && r.errors && r.errors.length ? r.errors : ['The bank could not be imported.'])); }
    } catch (e) {
      setDone(null); setErrors([e && e.message ? e.message : 'The bank could not be imported.']);
    } finally { setBusy(false); }
  };

  const onFile = async (e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!f) return;
    try { const body = await readFile(f); setText(body); await run(body); }
    catch { setDone(null); setErrors(['That file could not be read. Paste the reply instead.']); }
  };

  return (
    <div className="kqc-ai__import" data-tour="tpl-import">
      <div className="kqc-ai__import-head">Import the reply</div>
      <div className="kqc-ai__import-body">
        <textarea
          className={`input kqc-ai__paste${errors ? ' is-bad' : ''}`}
          value={text}
          onChange={(e) => { setText(e.target.value); setErrors(null); }}
          placeholder="Paste the assistant's reply here, from the first { to the last }"
          aria-label="The assistant's reply"
          spellCheck={false}
          disabled={busy}
        />
        <div className="kqc-row" style={{ gap: 10 }}>
          <button type="button" className="btn btn-primary" onClick={() => run(text)} disabled={!text.trim() || busy}>
            <svg className="kqi" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 4v11" /><path d="m7 10 5 5 5-5" /><path d="M4 19h16" /></svg>
            {busy ? 'Importing…' : 'Import as a bank'}
          </button>
          <label className={`btn btn-secondary${busy ? ' is-disabled' : ''}`} style={{ cursor: busy ? 'not-allowed' : 'pointer' }}>
            <svg className="kqi" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 3h9l5 5v13H5z" /><path d="M14 3v5h5" /></svg>
            Choose .json file
            <input type="file" accept=".json,application/json,text/plain" onChange={onFile} disabled={busy} style={{ display: 'none' }} aria-label="Choose the saved .json file" />
          </label>
        </div>

        {errors && (
          <div className="kqc-ai__bad" role="alert">
            <div className="kqc-ai__bad-head">Nothing was imported</div>
            <div className="kqc-ai__bad-body">
              {errors.map((er, i) => <div key={i}>{er}</div>)}
              <div className="kqc-small kqc-muted" style={{ marginTop: 4 }}>Ask the assistant to fix it and return JSON only, then paste again.</div>
            </div>
          </div>
        )}

        {done && (
          <div className="kqc-ai__ok" role="status">
            <svg className="kqi" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5" /></svg>
            <span>
              <strong>Imported {done.count} {done.count === 1 ? 'question' : 'questions'}.</strong>{' '}
              {done.attached
                ? <>&ldquo;{done.module}&rdquo; is now a bank and is attached to this template, so it can be used straight away.</>
                : <>&ldquo;{done.module}&rdquo; is now a bank, but it could not be attached to this template{done.why ? ` (${done.why})` : ''}. Add it from Edit questions.</>}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

export default function AiPromptBox({ onImport, ...props }) {
  const [copied, setCopied] = useState(false);
  const prompt = buildBankPrompt(props);
  const copy = async () => {
    try { await navigator.clipboard.writeText(prompt); } catch { /* the text is selectable below */ }
    setCopied(true);
    setTimeout(() => setCopied(false), 2200);
  };
  return (
    <div className="kqc-box kqc-ai" data-tour="tpl-ai">
      <div className="kqc-box__head">
        <svg className="kqi" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M12 3l1.9 4.6L18.5 9l-4.6 1.4L12 15l-1.9-4.6L5.5 9l4.6-1.4z" /><path d="M18 15l.9 2.1L21 18l-2.1.9L18 21l-.9-2.1L15 18l2.1-.9z" />
        </svg>
        <span>Need questions? Ask an AI</span>
        <span className="kqc-spacer" />
        <span className="kqc-ai__len">{prompt.length.toLocaleString()} characters</span>
      </div>
      <div className="kqc-box__body">
        <p className="kqc-small" style={{ color: 'var(--color-neutral-800)', margin: '0 0 14px' }}>
          {onImport
            ? <>This prompt is written from the template above &mdash; its name, question count, pass mark and modules. Paste it into any assistant, then bring the reply back here: it becomes a bank on this template.</>
            : <>This prompt is written from the template above &mdash; its name, question count, pass mark and modules. Paste it into any assistant, save the reply as <strong>.json</strong>, then upload it under Question banks.</>}
        </p>
        <div className="kqc-row" style={{ marginBottom: 12 }}>
          <button type="button" className="btn btn-primary btn-sm" onClick={copy}>
            <svg className="kqi" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="9" y="9" width="12" height="12" /><path d="M5 15H3V3h12v2" /></svg>
            {copied ? 'Copied' : 'Copy the prompt'}
          </button>
          <span className="kqc-small kqc-muted">Regenerates as you change the fields above.</span>
        </div>
        <pre className="kqc-ai__pre" tabIndex={0} aria-label="The prompt">{prompt}</pre>
        <ol className="kqc-ai__steps">
          <li><b>01</b><span>Copy the prompt and paste it into your assistant</span></li>
          {onImport ? (
            <>
              <li><b>02</b><span>Copy the whole reply &mdash; one per module</span></li>
              <li><b>03</b><span>Paste it below, or choose the saved <strong>.json</strong> file. We check it, then add it to this template</span></li>
            </>
          ) : (
            <>
              <li><b>02</b><span>Save the reply as a <strong>.json</strong> file &mdash; one per module</span></li>
              <li><b>03</b><span>Upload it under Question banks &mdash; we validate it before it is usable</span></li>
            </>
          )}
        </ol>
        <div className="kqc-ai__warn">
          <i aria-hidden="true" />
          <span>Read what it writes before you set it. A generated question is a draft &mdash; an assistant will state a wrong answer with total confidence, and your students will not know to doubt it.</span>
        </div>

        {onImport && <ImportReply onImport={onImport} />}
      </div>
    </div>
  );
}
